/**
 * Сборка ZIP-архива в браузере — без внешних зависимостей (задача 41).
 *
 * Зачем свой писатель: архив сканов собирает **браузер администратора**, а не сервер
 * (у HTTP-триггера функции ответ ограничен 3,5 МБ, время 15 с, а скан — до 10 МБ; см.
 * docs/tasks/41-admin-documents-download.md). Библиотеку для этого в проект тянуть
 * незачем: сканы — JPEG/PNG/PDF, они уже сжаты, поэтому архив собирается методом
 * **STORE** (без сжатия), а это значит «локальный заголовок + данные + центральный
 * каталог» — около сотни строк и ни одной новой зависимости.
 *
 * Формат: ZIP (не ZIP64) — архивы выгрузки по одному на команду, то есть заведомо
 * меньше 4 ГБ и 65 535 записей, поэтому 64-битные поля каталога не нужны.
 */
import { crc32 } from './crc32';

/** Запись архива: путь внутри ZIP и содержимое. */
export interface ZipEntry {
  /** Путь внутри архива: разделитель папок — `/` (требование формата ZIP). */
  path: string;
  /** Содержимое файла; буфер указан явно — `Blob` принимает только вид на `ArrayBuffer`. */
  bytes: Uint8Array<ArrayBuffer>;
  /** Момент изменения записи; по умолчанию — момент сборки архива. */
  time?: Date;
}

/** Размеры заголовков формата: локальный, центрального каталога и конца каталога. */
const LOCAL_HEADER_SIZE = 30;
const CENTRAL_HEADER_SIZE = 46;
const END_OF_CENTRAL_DIRECTORY_SIZE = 22;

/** Метод упаковки STORE (0) — без сжатия: сканы и так сжаты. */
const METHOD_STORE = 0;

/** Версия формата, которую объявляет архив: 2.0 — минимальная для STORE и папок. */
const VERSION = 20;

/** Флаг «имя записано в UTF-8» (general purpose bit 11): имена папок — кириллица. */
const UTF8_NAME_FLAG = 0x0800;

/** Сигнатуры записей формата. */
const SIGNATURE_LOCAL = 0x04034b50;
const SIGNATURE_CENTRAL = 0x02014b50;
const SIGNATURE_END = 0x06054b50;

/** Позиция (курсор) записи в буфер: поля формата пишутся подряд. */
class ByteWriter {
  private readonly view: DataView;
  private at = 0;

  /** bytes — буфер архива целиком (его размер посчитан заранее). */
  constructor(private readonly bytes: Uint8Array<ArrayBuffer>) {
    this.view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  }

  /** Записывает 16-битное поле без знака (little-endian — порядок полей ZIP). */
  u16(value: number): void {
    this.view.setUint16(this.at, value, true);
    this.at += 2;
  }

  /** Записывает 32-битное поле без знака. */
  u32(value: number): void {
    this.view.setUint32(this.at, value, true);
    this.at += 4;
  }

  /** Записывает байты (имя записи или её содержимое). */
  raw(value: Uint8Array): void {
    this.bytes.set(value, this.at);
    this.at += value.length;
  }

  /** Текущая позиция курсора — она же смещение локального заголовка записи. */
  get offset(): number {
    return this.at;
  }

  /** Собранный архив (буфер заполнен целиком). */
  finish(): Uint8Array<ArrayBuffer> {
    return this.bytes;
  }
}

/** Дата и время записи в формате MS-DOS: год отсчитывается от 1980. */
function dosTimestamp(time: Date): { date: number; time: number } {
  const year = Math.max(1980, time.getFullYear());
  return {
    date: ((year - 1980) << 9) | ((time.getMonth() + 1) << 5) | time.getDate(),
    time: (time.getHours() << 11) | (time.getMinutes() << 5) | Math.floor(time.getSeconds() / 2),
  };
}

/** Запись с посчитанными полями заголовков: имя, CRC, время и смещение в архиве. */
interface PreparedEntry {
  nameBytes: Uint8Array;
  bytes: Uint8Array;
  crc: number;
  date: number;
  time: number;
  /** Смещение локального заголовка от начала архива (нужно центральному каталогу). */
  offset: number;
}

/** Готовит запись: имя в UTF-8, CRC содержимого и время в формате DOS. */
function prepareEntry(entry: ZipEntry, fallbackTime: Date): PreparedEntry {
  const stamp = dosTimestamp(entry.time ?? fallbackTime);
  return {
    nameBytes: new TextEncoder().encode(entry.path),
    bytes: entry.bytes,
    crc: crc32(entry.bytes),
    date: stamp.date,
    time: stamp.time,
    offset: 0,
  };
}

/** Длина архива целиком: все локальные записи, все имена в каталоге и конец каталога. */
function archiveSize(prepared: readonly PreparedEntry[]): number {
  const local = prepared.reduce(
    (sum, entry) => sum + LOCAL_HEADER_SIZE + entry.nameBytes.length + entry.bytes.length,
    0,
  );
  const central = prepared.reduce(
    (sum, entry) => sum + CENTRAL_HEADER_SIZE + entry.nameBytes.length,
    0,
  );
  return local + central + END_OF_CENTRAL_DIRECTORY_SIZE;
}

/** Пишет локальный заголовок и содержимое записи: данные идут сразу после заголовка. */
function writeLocalEntry(writer: ByteWriter, entry: PreparedEntry): void {
  writer.u32(SIGNATURE_LOCAL);
  writer.u16(VERSION);
  writer.u16(UTF8_NAME_FLAG);
  writer.u16(METHOD_STORE);
  writer.u16(entry.time);
  writer.u16(entry.date);
  writer.u32(entry.crc);
  writer.u32(entry.bytes.length);
  writer.u32(entry.bytes.length);
  writer.u16(entry.nameBytes.length);
  writer.u16(0);
  writer.raw(entry.nameBytes);
  writer.raw(entry.bytes);
}

/** Пишет запись центрального каталога: заголовки всех файлов архива. */
function writeCentralEntry(writer: ByteWriter, entry: PreparedEntry): void {
  writer.u32(SIGNATURE_CENTRAL);
  writer.u16(VERSION);
  writer.u16(VERSION);
  writer.u16(UTF8_NAME_FLAG);
  writer.u16(METHOD_STORE);
  writer.u16(entry.time);
  writer.u16(entry.date);
  writer.u32(entry.crc);
  writer.u32(entry.bytes.length);
  writer.u32(entry.bytes.length);
  writer.u16(entry.nameBytes.length);
  writer.u16(0);
  writer.u16(0);
  writer.u16(0);
  writer.u16(0);
  writer.u32(0);
  writer.u32(entry.offset);
  writer.raw(entry.nameBytes);
}

/**
 * Собирает архив из записей: сначала все локальные записи (по ним архиву находят файлы),
 * затем центральный каталог с их описанием. Именно каталожная часть делает ZIP читаемым —
 * без неё получится просто «склеенные» файлы.
 */
export function zipArchive(
  entries: readonly ZipEntry[],
  now: Date = new Date(),
): Uint8Array<ArrayBuffer> {
  const prepared = entries.map((entry) => prepareEntry(entry, now));
  const writer = new ByteWriter(new Uint8Array(archiveSize(prepared)));

  const centralOffset = prepared.reduce((offset, entry) => {
    entry.offset = offset;
    writeLocalEntry(writer, entry);
    return writer.offset;
  }, 0);

  for (const entry of prepared) writeCentralEntry(writer, entry);

  const centralSize = writer.offset - centralOffset;
  writer.u32(SIGNATURE_END);
  writer.u16(0);
  writer.u16(0);
  writer.u16(prepared.length);
  writer.u16(prepared.length);
  writer.u32(centralSize);
  writer.u32(centralOffset);
  writer.u16(0);

  return writer.finish();
}